import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, Search, RefreshCw, AlertTriangle, X, Check, Pencil } from "lucide-react";
import { toast } from "sonner";
import { API, STAGES, CURRENCIES, SIGNED_REVENUE_STAGES } from "../lib/constants";
import { DealLedgerRow, fmtMoney } from "../components/DealLedgerRow";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import CanonicalMoneyStates from "../components/CanonicalMoneyStates";

const SIGNED_WON_STAGES = [...SIGNED_REVENUE_STAGES, "Closed \u2013 Won"];

const BUCKETS = [
  { key: "all", label: "All deals" },
  { key: "signed", label: "Signed" },
  { key: "proposal", label: "Proposals out" },
  { key: "open", label: "Open" },
  { key: "on_hold", label: "On hold" },
  { key: "lost", label: "Lost" },
];

const PERIODS = [
  { key: "all", label: "All dates" },
  { key: "p30", label: "Last 30" },
  { key: "p60", label: "Last 60" },
  { key: "p90", label: "Last 90" },
  { key: "30", label: "Next 30" },
  { key: "60", label: "Next 60" },
  { key: "90", label: "Next 90" },
];

const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

function Stat({ label, value, sub, tone = "text-slate-900", testId }) {
  return (
    <div className="min-w-[150px] flex-1" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`text-lg font-bold tabular-nums leading-tight ${tone}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-500">{sub}</p>}
    </div>
  );
}

const REPORTING_CHOICES = ["NGN", "USD", "GBP", "EUR"];

function RateInput({ currency, onSaved, compact }) {
  const [val, setVal] = useState("");
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  useEffect(() => {
    let live = true;
    fetch(`${API}/fx/working-rates/${currency}/suggestion`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null).then((d) => { if (live && d) setSuggestion(d.suggested_rate_to_ngn); })
      .catch(() => {});
    return () => { live = false; };
  }, [currency]);
  const save = async () => {
    const rate = parseFloat(val);
    if (!rate || rate <= 0) { toast.error("Enter a rate greater than zero"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/fx/working-rates/${currency}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rate }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || "Could not save the rate"); }
      toast.success(`Working rate saved: 1 ${currency} = ${rate.toLocaleString()} NGN`);
      onSaved && onSaved();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  return (
    <div className={`flex items-center gap-1.5 ${compact ? "" : "mt-1"}`} data-testid={`rate-input-${currency}`}>
      <span className="text-[11px]">1 {currency} =</span>
      <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={suggestion ? `${suggestion} (today's published)` : "NGN"}
        className="h-7 w-32 text-[11px] bg-white text-slate-900" data-testid={`rate-input-value-${currency}`} />
      <span className="text-[11px]">NGN</span>
      {suggestion != null && !val && (
        <button type="button" onClick={() => setVal(String(suggestion))} className="text-[11px] underline" data-testid={`rate-use-suggestion-${currency}`}>use {suggestion}</button>
      )}
      <Button size="sm" onClick={save} disabled={saving} className="h-7 text-[11px]" data-testid={`rate-save-${currency}`}>
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
      </Button>
    </div>
  );
}

function ConsolidatedHeadline({ c, currencies, reporting, onChange, onReload }) {
  const options = Array.from(new Set([...REPORTING_CHOICES, ...currencies]));
  const rates = Object.entries(c?.rates_used || {});
  const stale = c?.stale_currencies || [];
  return (
    <Card className="border-slate-900 bg-slate-900 text-white" data-testid="ledger-consolidated">
      <CardContent className="p-4 flex flex-wrap gap-4 items-start">
        <div className="w-full flex flex-wrap items-center gap-2">
          <p className="text-[10px] uppercase tracking-wider text-slate-300">Everything valued in</p>
          <select value={reporting} onChange={(e) => onChange(e.target.value)} className="h-6 text-xs rounded bg-white/10 border border-white/20 text-white px-1.5" data-testid="ledger-reporting-currency">
            {options.map((o) => <option key={o} value={o} className="text-slate-900">{o}</option>)}
          </select>
          <span className="text-[11px] text-slate-400">Indicative planning view. The per-currency figures below are the real contract values.</span>
        </div>
        {c ? (
          <>
            <Stat testId="ledger-consolidated-proposals" label="Proposals out" value={fmtMoney(c.proposal_value, c.currency)} sub={`${c.proposal_count} deals`} tone="text-amber-300" />
            <Stat testId="ledger-consolidated-signed" label="Signed" value={fmtMoney(c.signed_value, c.currency)} sub={`${c.signed_count} deals`} tone="text-emerald-300" />
            <Stat testId="ledger-consolidated-due30" label="Due next 30 days" value={fmtMoney(c.due_30, c.currency)} tone="text-white" />
            <Stat testId="ledger-consolidated-overdue" label="Overdue" value={fmtMoney(c.overdue, c.currency)} tone={c.overdue > 0 ? "text-rose-300" : "text-slate-400"} />
            <Stat testId="ledger-consolidated-invoiced" label="Invoiced, unpaid" value={fmtMoney(c.invoiced_outstanding, c.currency)} tone="text-sky-300" />
            <Stat testId="ledger-consolidated-collected" label="Collected this year" value={fmtMoney(c.collected_ytd, c.currency)} tone="text-emerald-300" />

            {/* Basis + the working rate in use, with who set it and when (visible, not hidden). */}
            <div className="w-full text-[11px] text-slate-400 flex flex-col gap-1" data-testid="ledger-basis">
              <span>{c.basis}</span>
              {rates.map(([k, v]) => (
                <span key={k} data-testid={`ledger-rate-${k}`}>
                  1 {k} = {Number(v.rate_to_ngn).toLocaleString()} NGN · set by {v.set_by || "unknown"} on {(v.set_at || "").slice(0, 10)}{v.stale ? " · out of date" : ""}
                </span>
              ))}
            </div>

            {/* A4: staleness surfaced plainly where it is used, with one-click update. */}
            {stale.length > 0 && (
              <div className="w-full rounded-md border border-amber-400/40 bg-amber-400/10 p-2.5 text-[11px] text-amber-200" data-testid="ledger-stale-rates">
                {stale.map((cur) => {
                  const v = c.rates_used[cur] || {};
                  return (
                    <div key={cur} className="flex flex-wrap items-center gap-2">
                      <span>{cur} deals are valued using a rate set on {(v.set_at || "").slice(0, 10)} by {v.set_by || "unknown"}. Update it?</span>
                      <RateInput currency={cur} onSaved={onReload} compact />
                    </div>
                  );
                })}
              </div>
            )}

            {/* A3: incomplete total names the missing currency and offers inline rate entry, recomputing at once. */}
            {c.incomplete && (
              <div className="w-full rounded-md border border-amber-400/40 bg-amber-400/10 p-2.5 text-[11px] text-amber-200" data-testid="ledger-consolidated-incomplete">
                <div className="flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold">This total is incomplete. I do not have a working rate for {(c.missing_rates || []).join(", ")}, so those deals are left out of the figures above.</p>
                    <div className="mt-2 space-y-1.5">
                      {(c.missing_rates || []).map((cur) => (
                        <div key={cur}>
                          <span className="mr-2">What rate are you using for {cur}?</span>
                          <RateInput currency={cur} onSaved={onReload} compact />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : <p className="text-xs text-slate-400">No deals to consolidate yet.</p>}
      </CardContent>
    </Card>
  );
}

function NeedsCurrencyCard({ items, onFix }) {
  if (!items || items.length === 0) return null;
  return (
    <Card className="border-amber-300 bg-amber-50" data-testid="ledger-needs-currency">
      <CardContent className="p-3 text-xs text-amber-800">
        <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />{items.length} deal{items.length === 1 ? "" : "s"} need a currency and are left out of every total</p>
        <ul className="mt-1.5 space-y-0.5">
          {items.map((it) => (
            <li key={it.opportunity_id} data-testid={`needs-currency-${it.opportunity_id}`}>
              <button type="button" onClick={() => onFix && onFix(it.opportunity_id)}
                className="text-left underline decoration-dotted hover:text-amber-900 inline-flex items-start gap-1"
                data-testid={`needs-currency-fix-${it.opportunity_id}`}>
                <Pencil className="w-3 h-3 mt-0.5 shrink-0" />
                <span>{it.company_name}{it.opportunity_name ? ` — ${it.opportunity_name}` : ""} ({new Intl.NumberFormat("en-GB").format(it.value || 0)}, currency not set). Set the currency on this deal to include it.</span>
              </button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

// F1 item 3 — correct a contradiction / missing-currency deal in place, using the same
// permissions as the deal page. Every save posts to /opportunities/{id}/f1-correct, which
// applies the change through the deal page's fan-out hooks AND writes a who/when/field/
// old/new entry to the deal's history. The payment control only marks a recorded payment
// Collected (after confirmation) — it never creates or changes an amount.
function DealCorrectionPanel({ deal, canEdit, onClose, onSaved }) {
  const [stage, setStage] = useState(deal.stage || "");
  const [value, setValue] = useState(deal.value || 0);
  const [currency, setCurrency] = useState(deal.currency || "");
  const [status, setStatus] = useState(deal.status || "Active");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [confirmPay, setConfirmPay] = useState(null); // payment entry awaiting confirmation
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveArmed, setLeaveArmed] = useState(false); // confirmation shown for leaving a signed/won stage

  const leavingSigned = SIGNED_WON_STAGES.includes(deal.stage) && !SIGNED_WON_STAGES.includes(stage);

  const loadHistory = async () => {
    const r = await fetch(`${API}/opportunities/${deal.opportunity_id}`, { credentials: "include" });
    if (r.ok) { const d = await r.json(); setHistory((d.stage_history || []).slice().reverse()); }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [deal.opportunity_id]);

  const putDeal = async (patch) => {
    const r = await fetch(`${API}/opportunities/${deal.opportunity_id}`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || `Save failed (${r.status})`); }
    return r.json();
  };
  const putPayment = async (entryId, patch) => {
    const r = await fetch(`${API}/opportunities/${deal.opportunity_id}/revenue-entries/${entryId}`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch) });
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || `Save failed (${r.status})`); }
    return r.json();
  };

  const markWon = () => { setStage("Closed \u2013 Won"); setStatus("Won"); };

  const save = async () => {
    if (leavingSigned && !leaveArmed) { setLeaveArmed(true); return; }  // ask for confirmation + reason first
    if (leavingSigned && !leaveReason.trim()) { toast.error("A reason is required to move a deal out of a signed/won stage"); return; }
    setSaving(true);
    try {
      const patch = {};
      if (stage !== deal.stage) patch.stage = stage;
      if (status !== (deal.status || "Active")) patch.status = status;
      if (Number(value) !== Number(deal.value || 0)) { patch.opportunity_value = Number(value); patch.contract_value = Number(value); }
      if (currency && currency !== deal.currency) patch.opportunity_currency = currency;
      if (stage === "Closed \u2013 Won" && !deal.signed_date) patch.actual_close_date = new Date().toISOString().slice(0, 10);
      if (leavingSigned && leaveReason.trim()) patch.stage_change_reason = leaveReason.trim();
      if (Object.keys(patch).length === 0) { toast.info("No changes to save"); setSaving(false); return; }
      await putDeal(patch);
      toast.success("Deal updated — recorded in its history");
      onSaved && onSaved();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const doMarkCollected = async (p) => {
    setSaving(true);
    try {
      await putPayment(p.entry_id, { status: "Collected" });
      toast.success("Payment marked Collected — recorded in its history");
      setConfirmPay(null);
      await loadHistory();
      onSaved && onSaved();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const histLabel = (h) => {
    const f = h.field || h.kind || "change";
    if (h.field) return `${f}: ${String(h.from ?? "—")} → ${String(h.to ?? "—")}`;
    if (h.from || h.to) return `stage: ${String(h.from ?? "—")} → ${String(h.to ?? "—")}`;
    return f;
  };
  const histWho = (h) => h.changed_by || h.actor || "system";

  const payments = deal.payments || [];
  const cur = deal.currency || currency || "";
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="deal-correction-panel">
        <DialogHeader>
          <DialogTitle className="text-base">{deal.company_name}{deal.opportunity_name ? ` — ${deal.opportunity_name}` : ""}</DialogTitle>
        </DialogHeader>
        {!canEdit && <p className="text-xs text-rose-600" data-testid="panel-readonly">You have view-only access; corrections need editor permission (same as the deal page).</p>}
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between gap-2">
            <label className="text-xs text-slate-500 w-24 shrink-0">Stage</label>
            <select className="h-8 text-xs border rounded px-1 flex-1" value={stage} disabled={!canEdit}
              data-testid="panel-stage" onChange={(e) => setStage(e.target.value)}>
              {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <Button size="sm" variant="outline" className="h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              disabled={!canEdit} data-testid="panel-mark-won" onClick={markWon}><Check className="w-3 h-3 mr-1" />Mark won</Button>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500 w-24 shrink-0">Value</label>
            <Input type="number" className="h-8 text-xs flex-1" value={value} disabled={!canEdit}
              data-testid="panel-value" onChange={(e) => setValue(e.target.value)} />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500 w-24 shrink-0">Currency</label>
            <select className="h-8 text-xs border rounded px-1 flex-1" value={currency || ""} disabled={!canEdit}
              data-testid="panel-currency" onChange={(e) => setCurrency(e.target.value)}>
              <option value="">— not set —</option>
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-1">Recorded payments linked to this deal ({payments.length}) — this only marks a payment Collected; it never changes the amount.</p>
            {payments.length === 0 ? (
              <p className="text-xs text-slate-400" data-testid="panel-no-payments">No payments recorded.</p>
            ) : (
              <ul className="space-y-1" data-testid="panel-payments">
                {payments.map((p) => (
                  <li key={p.entry_id} className="flex items-center gap-2 text-xs border rounded px-2 py-1" data-testid={`panel-payment-${p.entry_id}`}>
                    <span className="text-slate-500">{p.scheduled_date || "—"}</span>
                    <span className="font-medium">{fmtMoney(p.amount, cur)}</span>
                    <span className="ml-auto text-slate-500">{p.status}</span>
                    {canEdit && (p.status === "Forecast" || p.status === "Invoiced") && (
                      <Button size="sm" variant="outline" className="h-6 text-[11px] border-emerald-300 text-emerald-700"
                        data-testid={`panel-collect-${p.entry_id}`} onClick={() => setConfirmPay(p)}>
                        Mark collected
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {confirmPay && (
              <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs" data-testid="panel-payment-confirm">
                <p className="text-amber-900">Mark this recorded payment as Collected? The amount is not changed.</p>
                <p className="mt-1 font-medium">{fmtMoney(confirmPay.amount, cur)} · {cur || "no currency"} · due {confirmPay.scheduled_date || "—"}</p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" className="h-7 text-xs" disabled={saving} data-testid="panel-payment-confirm-yes" onClick={() => doMarkCollected(confirmPay)}>Yes, mark Collected</Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="panel-payment-confirm-no" onClick={() => setConfirmPay(null)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-1">History (who and when)</p>
            {history.length === 0 ? (
              <p className="text-xs text-slate-400" data-testid="panel-history-empty">No history yet.</p>
            ) : (
              <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="panel-history">
                {history.map((h, i) => (
                  <li key={i} className="text-xs text-slate-600 border-l-2 border-slate-200 pl-2" data-testid="panel-history-entry">
                    <span className="font-medium text-slate-800">{histLabel(h)}</span>
                    {h.note ? <span className="text-slate-500"> · {h.note}</span> : ""}
                    <span className="text-slate-400"> · by {histWho(h)}</span>
                    <span className="text-slate-400"> · {(h.changed_at || "").slice(0, 16).replace("T", " ")}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-2">
          <Button size="sm" variant="outline" onClick={onClose} data-testid="panel-cancel"><X className="w-3 h-3 mr-1" />Cancel</Button>
          <Button size="sm" disabled={!canEdit || saving} onClick={save} data-testid="panel-save">Save</Button>
        </div>
        {leaveArmed && leavingSigned && (
          <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs" data-testid="panel-leave-signed-confirm">
            <p className="text-amber-900 font-medium">Move this deal out of a signed/won stage ({deal.stage} → {stage})?</p>
            <p className="text-amber-800 mt-0.5">This reverses a signed deal. Give a reason — it is recorded in the deal's history with who and when.</p>
            <textarea className="text-sm border rounded px-2 py-1 w-full mt-1" rows={2} placeholder="Reason for leaving signed…"
              data-testid="panel-leave-reason" value={leaveReason} onChange={(e) => setLeaveReason(e.target.value)} />
            <div className="flex gap-2 mt-1">
              <Button size="sm" className="h-7 text-xs" disabled={saving || !leaveReason.trim()} data-testid="panel-leave-confirm" onClick={save}>Confirm and save</Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" data-testid="panel-leave-cancel" onClick={() => { setLeaveArmed(false); setLeaveReason(""); setStage(deal.stage); }}>Cancel</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function CollectedNgnCard({ cn, onFix }) {
  if (!cn) return null;
  const rn = cn.rate_needed || [];
  if (!cn.count && rn.length === 0) return null;
  return (
    <Card className="border-emerald-200 bg-emerald-50/60" data-testid="ledger-collected-ngn">
      <CardContent className="p-3 text-xs text-emerald-900">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">Collected · naira total (frozen)</span>
          <span className="text-lg font-bold tabular-nums text-emerald-800" data-testid="collected-ngn-total">{fmtMoney(cn.total, "NGN")}</span>
          <span className="text-emerald-700">from {cn.count} payment{cn.count === 1 ? "" : "s"}</span>
        </div>
        <p className="text-[11px] text-emerald-700 mt-0.5">{cn.basis}</p>
        {rn.length > 0 && (
          <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-800" data-testid="collected-ngn-rate-needed">
            <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />{rn.length} collected payment{rn.length === 1 ? "" : "s"} need a rate and {rn.length === 1 ? "is" : "are"} left out of the naira total</p>
            <ul className="mt-1 space-y-0.5">
              {rn.map((it, idx) => (
                <li key={`${it.opportunity_id}-${it.entry_id}-${idx}`} data-testid={`rate-needed-${it.entry_id}`}>
                  <button type="button" onClick={() => onFix && onFix(it.opportunity_id)} className="text-left underline decoration-dotted hover:text-amber-900 inline-flex items-start gap-1" data-testid={`rate-needed-fix-${it.entry_id}`}>
                    <Pencil className="w-3 h-3 mt-0.5 shrink-0" />
                    <span>{it.company_name}{it.opportunity_name ? ` — ${it.opportunity_name}` : ""}: {fmtMoney(it.amount, it.currency)} collected{it.collected_date ? ` on ${it.collected_date}` : ""}, no working rate for {it.currency} that day. Set a rate to include it.</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CurrencySummary({ currency, t }) {
  return (
    <Card className="border-slate-200" data-testid={`ledger-summary-${currency}`}>
      <CardContent className="p-4 flex flex-wrap gap-4 items-start">
        <div className="w-14 shrink-0"><span className="inline-block rounded-md bg-slate-900 text-white text-xs font-bold px-2 py-1">{currency}</span></div>
        <Stat testId={`ledger-${currency}-proposals`} label="Proposals out" value={fmtMoney(t.proposal_value, currency)} sub={`${t.proposal_count} deal${t.proposal_count === 1 ? "" : "s"}`} tone="text-amber-700" />
        <Stat testId={`ledger-${currency}-signed`} label="Signed" value={fmtMoney(t.signed_value, currency)} sub={`${t.signed_count} deal${t.signed_count === 1 ? "" : "s"}`} tone="text-emerald-700" />
        <Stat testId={`ledger-${currency}-due30`} label="Due next 30 days" value={fmtMoney(t.due_30, currency)} />
        <Stat testId={`ledger-${currency}-overdue`} label="Overdue" value={fmtMoney(t.overdue, currency)} tone={t.overdue > 0 ? "text-rose-600" : "text-slate-400"} />
        <Stat testId={`ledger-${currency}-invoiced`} label="Invoiced, unpaid" value={fmtMoney(t.invoiced_outstanding, currency)} tone="text-blue-700" />
        <Stat testId={`ledger-${currency}-collected`} label="Collected this year" value={fmtMoney(t.collected_ytd, currency)} tone="text-emerald-700" />
        {t.unscheduled_signed > 0 && <Stat testId={`ledger-${currency}-unscheduled`} label="Signed, not scheduled" value={fmtMoney(t.unscheduled_signed, currency)} tone="text-amber-700" sub="add payment dates" />}
      </CardContent>
    </Card>
  );
}

export default function RevenuePage({ embedded = false } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [bucket, setBucket] = useState("all");
  const [currency, setCurrency] = useState("all");
  const [q, setQ] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [period, setPeriod] = useState("all");
  const [reporting, setReporting] = useState(() => localStorage.getItem("ledger_reporting_currency") || "NGN");
  const [error, setError] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [panelDealId, setPanelDealId] = useState(null);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" }).then((r) => r.ok ? r.json() : null)
      .then((u) => setCanEdit(["admin", "user"].includes(u?.role)))
      .catch(() => setCanEdit(false));
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await fetch(`${API}/opportunities/metrics/deal-ledger?reporting_currency=${encodeURIComponent(reporting)}`, { credentials: "include" });
      if (r.ok) {
        const d = await r.json();
        if (!d || !Array.isArray(d.rows)) { setError("The deal ledger came back in an unexpected form. This can happen just after an update — reload to get the latest."); return; }
        setError(null);
        setData(d);
      } else {
        setError(`Could not load the deal ledger (error ${r.status}).`);
        toast.error("Could not load the deal ledger");
      }
    } catch (e) {
      setError("Could not reach the server to load the deal ledger.");
    } finally {
      setLoading(false);
    }
  }, [reporting]);
  useEffect(() => { load(); }, [load]);
  const changeReporting = (v) => { localStorage.setItem("ledger_reporting_currency", v); setReporting(v); };

  const call = async (path, method, body) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}${path}`, { method, credentials: "include", headers: { "Content-Type": "application/json" }, body: body ? JSON.stringify(body) : undefined });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e?.detail || `Request failed (${r.status})`); }
      await load(true);
      return true;
    } catch (e) {
      toast.error(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const onAddPayment = async (oppId, payload) => (await call(`/opportunities/${oppId}/revenue-entries`, "POST", payload)) && toast.success("Payment added");
  const onUpdatePayment = async (oppId, entryId, patch) => (await call(`/opportunities/${oppId}/revenue-entries/${entryId}`, "PUT", patch)) && toast.success(patch.status ? `Marked ${patch.status.toLowerCase()}` : "Payment updated");
  const onDeletePayment = async (oppId, entryId) => {
    if (!window.confirm("Remove this payment from the schedule?")) return;
    (await call(`/opportunities/${oppId}/revenue-entries/${entryId}`, "DELETE")) && toast.success("Payment removed");
  };
  const onMarkSigned = async (oppId) => (await call(`/opportunities/${oppId}`, "PUT", { stage: "Contract Signed", actual_close_date: new Date().toISOString().slice(0, 10) })) && toast.success("Marked as signed");
  const onHoldDelivery = async (oppId, state) => (await call(`/opportunities/${oppId}/delivery/hold`, "POST", { state })) && toast.success(state === "paused" ? "Engagement paused — unpaid money held" : "Set to signed, not started — unpaid money held");
  const onStartDelivery = async (oppId, startDate) => (await call(`/opportunities/${oppId}/delivery/start`, "POST", { start_date: startDate })) && toast.success("Delivery started — dates restored, spacing kept");
  const onOverrideEntry = async (oppId, entryId, payload) => (await call(`/opportunities/${oppId}/revenue-entries/${entryId}/override`, "POST", payload)) && toast.success(payload.mode === "hold" ? "Entry held" : payload.mode === "due_now" ? "Entry marked due now" : "Entry re-dated");

  // M1/L1 — money DUE in a window: unpaid (Forecast/Invoiced) payments whose due date
  // falls in the window. Forward keys ("30") look ahead [today, today+N]; past keys
  // ("p30") look behind [today-N, today] — money that FELL due in that window, counted
  // the same way. Returns the amount in the row's own currency.
  const rowDueInWindow = useCallback((r, key) => {
    if (!data || !r.payments || key === "all") return 0;
    const past = String(key).startsWith("p");
    const n = Number(past ? String(key).slice(1) : key);
    const start = past ? addDays(data.today, -n) : data.today;
    const end = past ? data.today : addDays(data.today, n);
    return r.payments.reduce((s, p) => {
      const due = (p.scheduled_date || "").slice(0, 10);
      const unpaid = p.status === "Forecast" || p.status === "Invoiced";
      return unpaid && due && due >= start && due <= end ? s + (Number(p.amount) || 0) : s;
    }, 0);
  }, [data]);

  const periodCount = useCallback((key) => {
    if (!data) return 0;
    if (key === "all") return data.rows.length;
    return data.rows.filter((r) => rowDueInWindow(r, key) > 0).length;
  }, [data, rowDueInWindow]);

  // Totals of money due in the selected window, per currency (real per-currency values).
  const periodTotals = useMemo(() => {
    if (!data || period === "all") return null;
    const by = {};
    let count = 0;
    data.rows.forEach((r) => {
      const amt = rowDueInWindow(r, period);
      if (amt > 0) { by[r.currency || "?"] = (by[r.currency || "?"] || 0) + amt; count += 1; }
    });
    return { by, count };
  }, [data, period, rowDueInWindow]);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.rows.filter((r) =>
      (bucket === "all" || r.bucket === bucket) &&
      (currency === "all" || r.currency === currency) &&
      (!overdueOnly || r.overdue > 0) &&
      (period === "all" || rowDueInWindow(r, period) > 0) &&
      (!needle || `${r.company_name} ${r.opportunity_name} ${r.owner || ""}`.toLowerCase().includes(needle)));
  }, [data, bucket, currency, q, overdueOnly, period]);

  const overdueDeals = data ? data.rows.filter((r) => r.overdue > 0).length : 0;

  // F1 item 2 — the tab counts stay global (unchanged); when the table is empty we
  // explain the active filters and offer a way out, rather than a bare "no match".
  const bucketLabel = (k) => (BUCKETS.find((b) => b.key === k) || {}).label || k;
  const activeFilters = [];
  if (bucket !== "all") activeFilters.push({ key: "bucket", label: `Tab: ${bucketLabel(bucket)}`, clear: () => setBucket("all") });
  if (currency !== "all") activeFilters.push({ key: "currency", label: `Currency: ${currency}`, clear: () => setCurrency("all") });
  if (overdueOnly) activeFilters.push({ key: "overdue", label: "Overdue only", clear: () => setOverdueOnly(false) });
  if (period !== "all") activeFilters.push({ key: "period", label: `Due: ${(PERIODS.find((p) => p.key === period) || {}).label || period}`, clear: () => setPeriod("all") });
  if (q.trim()) activeFilters.push({ key: "search", label: `Search: "${q.trim()}"`, clear: () => setQ("") });
  const clearFilters = () => { setBucket("all"); setCurrency("all"); setOverdueOnly(false); setPeriod("all"); setQ(""); };
  const panelDeal = panelDealId && data ? data.rows.find((r) => r.opportunity_id === panelDealId) : null;

  return (
    <div className={embedded ? "space-y-4" : "p-6 space-y-4"} data-testid="financials-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          {!embedded && <h1 className="text-2xl font-bold text-slate-900">Financials</h1>}
          <p className="text-xs text-slate-500">Every deal with a proposal out or a contract signed, its payment schedule, and what is due, overdue or collected. Per-currency totals are exact; the dark headline converts everything to one reporting currency.</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => load()} disabled={loading} data-testid="ledger-refresh"><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
      </div>

      {/* Canonical four states — the SAME source as the Commercial Dashboard funnel,
          so the two screens agree. The per-deal ledger below is supporting detail. */}
      <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="financials-canonical">
        <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">Money — four states (canonical)</p>
        <CanonicalMoneyStates />
      </div>

      {error && !data ? (
        <Card className="border-rose-200 bg-rose-50" data-testid="ledger-error">
          <CardContent className="p-6 flex items-center gap-3 text-rose-700">
            <AlertTriangle className="h-4 w-4" />
            <span className="text-sm flex-1">{error}</span>
            <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" onClick={() => load()} data-testid="ledger-error-retry"><RefreshCw className="w-3.5 h-3.5 mr-1" />Retry</Button>
          </CardContent>
        </Card>
      ) : loading || !data ? (
        <Card className="border-slate-200"><CardContent className="p-6 flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading deals…</CardContent></Card>
      ) : (
        <>
          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 flex items-center gap-2 text-xs text-rose-700" data-testid="ledger-error-banner">
              <AlertTriangle className="w-3.5 h-3.5" /><span className="flex-1">{error} Showing the last figures loaded.</span>
              <Button size="sm" variant="outline" className="h-7 text-xs border-rose-300 text-rose-700" onClick={() => load()} data-testid="ledger-error-banner-retry">Retry</Button>
            </div>
          )}
          {data.contradictions?.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="ledger-contradictions">
              <p className="text-xs font-semibold text-amber-800 mb-1">Contradictions to review ({data.contradictions.length}) — shown as recorded, not reclassified</p>
              <ul className="space-y-1">
                {data.contradictions.map((c) => (
                  <li key={c.opportunity_id} className="text-xs text-slate-700" data-testid={`contradiction-${c.opportunity_id}`}>
                    <button type="button" onClick={() => setPanelDealId(c.opportunity_id)}
                      className="text-left underline decoration-dotted hover:text-amber-900 inline-flex items-start gap-1"
                      data-testid={`contradiction-fix-${c.opportunity_id}`}>
                      <Pencil className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>
                        <span className="font-semibold">{c.company_name}</span>
                        {c.opportunity_name ? ` — ${c.opportunity_name}` : ""}: {c.label}
                        <span className="text-slate-500"> (stage: {c.stage || "—"}, {c.currency || "?"} {Math.round(c.collected).toLocaleString()} collected)</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="grid gap-3" data-testid="ledger-summaries">
            <NeedsCurrencyCard items={data.needs_currency} onFix={setPanelDealId} />
            <CollectedNgnCard cn={data.collected_ngn} onFix={setPanelDealId} />
            {data.currencies.length === 0 && <p className="text-sm text-slate-400">No proposal-stage or signed deals yet.</p>}
          </div>

          {overdueDeals > 0 && (
            <button type="button" onClick={() => setOverdueOnly((v) => !v)} className={`w-full text-left rounded-lg border px-3 py-2 text-xs flex items-center gap-2 transition-colors ${overdueOnly ? "bg-rose-100 border-rose-300 text-rose-800" : "bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100"}`} data-testid="ledger-overdue-banner">
              <AlertTriangle className="w-3.5 h-3.5" />
              {overdueDeals} deal{overdueDeals === 1 ? " has" : "s have"} overdue payments. {overdueOnly ? "Showing overdue only, click to show all." : "Click to show only these."}
            </button>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" role="tablist" data-testid="ledger-bucket-filter">
              {BUCKETS.map((b) => {
                const n = b.key === "all" ? data.rows.length : data.rows.filter((r) => r.bucket === b.key).length;
                return (
                  <button key={b.key} type="button" onClick={() => setBucket(b.key)} className={`px-2.5 py-1 text-xs rounded transition-colors ${bucket === b.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`ledger-bucket-${b.key}`}>
                    {b.label} <span className="opacity-60">{n}</span>
                  </button>
                );
              })}
            </div>
            {data.currencies.length > 1 && (
              <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="ledger-currency-filter">
                {["all", ...data.currencies].map((c) => (
                  <button key={c} type="button" onClick={() => setCurrency(c)} className={`px-2.5 py-1 text-xs rounded uppercase transition-colors ${currency === c ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`ledger-currency-${c}`}>{c}</button>
                ))}
              </div>
            )}
            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5" data-testid="ledger-period-filter" title="Filter to deals with money due in the window">
              {PERIODS.map((p) => (
                <button key={p.key} type="button" onClick={() => setPeriod(p.key)} className={`px-2.5 py-1 text-xs rounded transition-colors ${period === p.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`ledger-period-${p.key}`}>
                  {p.label} <span className="opacity-60">{periodCount(p.key)}</span>
                </button>
              ))}
            </div>
            <div className="relative ml-auto">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, deal or owner" className="h-8 pl-7 text-xs w-64" data-testid="ledger-search" />
            </div>
          </div>

          {activeFilters.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="active-filters-bar">
              <span className="text-[11px] text-slate-500">Filters on:</span>
              {activeFilters.map((f) => (
                <span key={f.key} className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[11px] text-slate-700" data-testid={`bar-filter-${f.key}`}>
                  {f.label}
                  <button type="button" onClick={f.clear} className="hover:text-rose-600" data-testid={`bar-filter-clear-${f.key}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
              <button type="button" onClick={clearFilters} className="text-[11px] underline text-slate-600 hover:text-slate-900 ml-1" data-testid="bar-clear-filters">Clear filters</button>
            </div>
          )}

          {periodTotals && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="period-totals">
              <span className="font-semibold text-slate-700">Due {(PERIODS.find((p) => p.key === period) || {}).label?.toLowerCase()}:</span>
              <span className="text-slate-500" data-testid="period-totals-count">{periodTotals.count} deal{periodTotals.count === 1 ? "" : "s"}</span>
              {Object.keys(periodTotals.by).length === 0 ? (
                <span className="text-slate-400">Nothing due in this window.</span>
              ) : (
                Object.entries(periodTotals.by).map(([cur, amt]) => (
                  <span key={cur} className="tabular-nums font-medium text-slate-800" data-testid={`period-total-${cur}`}>{fmtMoney(amt, cur === "?" ? undefined : cur)}</span>
                ))
              )}
              <span className="text-[10px] text-slate-400 ml-auto">Money forecast or invoiced with a due date inside the window. Per-currency, never mixed.</span>
            </div>
          )}

          <Card className="border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="ledger-table">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                    <th className="py-2 pl-2" />
                    <th className="py-2 text-left">Client / deal</th>
                    <th className="py-2 text-left">Status</th>
                    <th className="py-2 pr-4 text-right">Value</th>
                    <th className="py-2 pl-2 text-left">Signed / expected</th>
                    <th className="py-2 text-left">Next due</th>
                    <th className="py-2 text-right">Overdue</th>
                    <th className="py-2 text-right">Outstanding</th>
                    <th className="py-2 pr-2 text-right">Collected</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={9} className="py-8 text-center" data-testid="ledger-empty">
                      {error ? (
                        <div className="flex flex-col items-center gap-2 text-rose-700" data-testid="ledger-empty-error">
                          <span className="text-xs">{error}</span>
                          <Button size="sm" variant="outline" className="h-7 text-xs border-rose-300 text-rose-700" onClick={() => load()} data-testid="ledger-empty-retry"><RefreshCw className="w-3.5 h-3.5 mr-1" />Retry</Button>
                        </div>
                      ) : activeFilters.length > 0 ? (
                        <div className="flex flex-col items-center gap-2" data-testid="ledger-empty-filtered">
                          <span className="text-xs text-slate-500">No deals match the active filter{activeFilters.length > 1 ? "s" : ""}. The tab counts show every deal; these filters are hiding them:</span>
                          <div className="flex flex-wrap items-center justify-center gap-1.5">
                            {activeFilters.map((f) => (
                              <span key={f.key} className="inline-flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[11px] text-slate-700" data-testid={`active-filter-${f.key}`}>
                                {f.label}
                                <button type="button" onClick={f.clear} className="hover:text-rose-600" data-testid={`active-filter-clear-${f.key}`}><X className="w-3 h-3" /></button>
                              </span>
                            ))}
                          </div>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={clearFilters} data-testid="ledger-clear-filters">Clear filters</Button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">No deals match these filters.</span>
                      )}
                    </td></tr>
                  ) : rows.map((r) => (
                    <DealLedgerRow key={r.opportunity_id} deal={r} categories={data.categories} busy={busy}
                      onAddPayment={onAddPayment} onUpdatePayment={onUpdatePayment} onDeletePayment={onDeletePayment} onMarkSigned={onMarkSigned}
                      onHoldDelivery={onHoldDelivery} onStartDelivery={onStartDelivery} onOverrideEntry={onOverrideEntry}
                      canAct={canEdit} onStatusChanged={() => load(true)} />
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
      {panelDeal && (
        <DealCorrectionPanel deal={panelDeal} canEdit={canEdit}
          onClose={() => setPanelDealId(null)}
          onSaved={async () => { await load(true); setPanelDealId(null); }} />
      )}
    </div>
  );
}
